import { Music, Edit2, Trash2, Upload, Link as LinkIcon, CheckCircle, Mail, RefreshCw, BadgeCheck } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';

function ArtistAdminActions({ artist, handleApproveArtist, handleSendSetupEmail, handleRegenerateToken, handleToggleVerification, setAudioArtistId, setAudioDialogOpen, setLinkArtistId, setLinkDialogOpen, openArtistDialog, handleDeleteArtist }) {
  return (
    <div className="flex items-center gap-1">
      {artist.status === 'pending' && (
        <Button size="icon" variant="ghost" onClick={() => handleApproveArtist(artist.id)} className="h-8 w-8 text-yellow-400 hover:text-yellow-300" title="Approve artist"><CheckCircle className="w-4 h-4" /></Button>
      )}
      {artist.email && !artist.setup_token_used && (
        <Button size="icon" variant="ghost" onClick={() => handleSendSetupEmail(artist.id)} className="h-8 w-8 text-green-400 hover:text-green-300" title="Send setup email" data-testid={`send-email-${artist.id}`}><Mail className="w-4 h-4" /></Button>
      )}
      <Button size="icon" variant="ghost" onClick={() => handleRegenerateToken(artist.id)} className="h-8 w-8 text-orange-400 hover:text-orange-300" title="Regenerate login code" data-testid={`regenerate-token-${artist.id}`}><RefreshCw className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => handleToggleVerification(artist.id)} className={`h-8 w-8 ${artist.is_verified ? 'text-blue-400' : 'text-[#666]'}`} title={artist.is_verified ? 'Remove verification' : 'Verify artist'} data-testid={`verify-btn-${artist.id}`}><BadgeCheck className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => { setAudioArtistId(artist.id); setAudioDialogOpen(true); }} className="h-8 w-8" title="Upload audio sample" data-testid={`upload-audio-${artist.id}`}><Upload className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => { setLinkArtistId(artist.id); setLinkDialogOpen(true); }} className="h-8 w-8" title="Link to user account" data-testid={`link-${artist.id}`}><LinkIcon className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => openArtistDialog(artist)} className="h-8 w-8" data-testid={`edit-${artist.id}`}><Edit2 className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => handleDeleteArtist(artist.id)} className="h-8 w-8 text-red-400 hover:text-red-300" data-testid={`delete-${artist.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
}

export function ArtistAdminCard({ artist, handlers }) {
  return (
    <div className="bg-surface border border-[#262626] rounded-xl overflow-hidden" data-testid={`artist-admin-${artist.id}`}>
      <div className="aspect-video relative">
        <img src={artist.image_url || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400'} alt={artist.name} className="w-full h-full object-cover" />
        <div className="absolute top-2 right-2 flex gap-1">
          {artist.is_verified && <Badge className="bg-blue-500/20 text-blue-400 border-0"><BadgeCheck className="w-3 h-3" /></Badge>}
          <Badge className={artist.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>{artist.is_active ? 'Active' : 'Inactive'}</Badge>
        </div>
        <div className="absolute top-2 left-2">
          <Badge className={artist.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>{artist.status || 'legacy'}</Badge>
        </div>
        {artist.sample_audio_url && (
          <div className="absolute bottom-2 left-2"><Badge className="bg-purple-500/20 text-purple-400"><Music className="w-3 h-3 mr-1" />Audio</Badge></div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-bold">{artist.name}</h3>
          <span className="font-mono text-lime">${(artist.price || 0).toFixed(2)}</span>
        </div>
        <p className="text-sm text-[#A1A1AA] mb-2 line-clamp-2">{artist.bio || 'No bio'}</p>
        <p className="text-xs text-[#666] mb-1">{artist.email || <span className="text-yellow-400">No email added</span>}</p>
        {artist.email ? (
          <p className="text-xs text-[#666] mb-4">{artist.setup_token_used ? <span className="text-green-400">Setup complete</span> : <span className="text-yellow-400">Pending setup</span>}</p>
        ) : (
          <div className="mb-4" />
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch checked={artist.is_active} onCheckedChange={() => handlers.handleToggleActive(artist)} data-testid={`toggle-${artist.id}`} />
            <span className="text-xs text-[#666]">Active</span>
          </div>
          <ArtistAdminActions artist={artist} {...handlers} />
        </div>
      </div>
    </div>
  );
}
